import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { TRANSLATE_PROPOSED_ANSWER } from "../graphql/sharedDocuments";
import { useProposedAnswerRevisions, type ProposedAnswerRevision } from "./useProposedAnswerRevisions";
import {
    useAnswerLanguageSelection,
    type AnswerLanguageDisplay,
} from "./useAnswerLanguageSelection";

interface UseProposedAnswerOptions {
    messageId: string | null | undefined;
    proposedAnswer: string | null | undefined;
    /** The message's full revision history (drives the history dropdown). */
    revisions: ProposedAnswerRevision[] | null | undefined;
    currentRevisionId: string | null | undefined;
    /** Called after selecting a revision or after a translation landed -- both
     * move the server-side revision pointer, so the caller should refetch to pick
     * up the new `revisions`/`currentRevisionId`. Purely a background sync:
     * `display` below never waits on it. */
    onChanged?: () => void;
    /** The incoming mail's detected language (spec rules 2.2, 2.6). */
    detectedLanguage?: string | null | undefined;
    /** Live generation stream text, or null when nothing is streaming. */
    streamText?: string | null;
    /** Positive evidence that a generation is producing right now (excluding a
     * stale `Pending` left over from a reload). Drives rule 3.5 and the
     * stale-answer suppression. */
    synthesisActive?: boolean;
    /** Reports a failed translation to the operator (rule 3.7). */
    onTranslationError?: (message: string) => void;
}

interface UseProposedAnswerReturn {
    sortedRevisions: ProposedAnswerRevision[];
    currentRevisionId: string | null | undefined;
    /** The revision object the pointer is on (for labeling the history
     * dropdown's "current" entry, e.g. with its timestamp). */
    viewingRevision: ProposedAnswerRevision | null;
    /** True when the pointer is parked behind the tip. Informational only
     * (e.g. gating a "regeneration in progress" indicator that only applies
     * at the tip) -- it does not gate which text or actions are available. */
    isViewingOlderVersion: boolean;
    isSelecting: boolean;
    /** The single source of truth for what to show: the answer in the selected
     * language, or a loading state (spec rule 1.2). Nothing else is held aside
     * for later reconciliation. */
    display: AnswerLanguageDisplay;
    /** Convenience alias for `display.text`. */
    displayAnswer: string | null;
    selectRevision: (revisionId: string) => void;
    getSourceLabel: (source: string) => string;
    /** Adopt text the operator just produced another way (e.g. a manual answer
     * just saved) -- shows it immediately, bypassing any in-flight refetch,
     * the same way selecting a version does (rule 1.5). */
    adoptText: (text: string, language: string | null | undefined) => void;
    /** The selected language (rule 2.1: only ever changed by an operator action). */
    selectedLanguage: string;
    /** The offered languages: a fixed set plus the mail's detected one (rule 2.6). */
    languageOptions: string[];
    selectLanguage: (code: string) => void;
    isTranslating: boolean;
    /** Rule 6.3: shown when there is an answer to translate, or while a
     * generation is running; hidden on a settled, empty answer. */
    languageControlVisible: boolean;
    /** Rule 6.2: call after a paragraph dismissal -- everything held predates it,
     * so the answer in the selected language is re-requested. */
    answerReplaced: () => void;
}

/**
 * Everything the answer area needs: which revision the pointer is on, which
 * language is selected, and the one text that follows from both.
 *
 * Both surfaces (Neptune's ProposedAnswerTab, Venus's AgentProposedAnswer) drive
 * their answer area from this hook, which is what makes them behave identically
 * (spec rule 2.7). The language state machine lives in
 * `useAnswerLanguageSelection`; this composes it with revision selection and the
 * translate mutation.
 */
export function useProposedAnswer({
    messageId,
    proposedAnswer,
    revisions,
    currentRevisionId: currentRevisionIdProp,
    onChanged,
    detectedLanguage,
    streamText = null,
    synthesisActive = false,
    onTranslationError,
}: UseProposedAnswerOptions): UseProposedAnswerReturn {
    const {
        sortedRevisions,
        currentRevisionId,
        viewingRevision,
        isViewingOlderVersion,
        selectRevision: selectRevisionMutation,
        getSourceLabel,
    } = useProposedAnswerRevisions({
        messageId,
        currentRevisionId: currentRevisionIdProp,
        revisions,
        onSelected: onChanged,
    });

    const [translateMutation] = useMutation(TRANSLATE_PROPOSED_ANSWER);

    // The language the backend's current answer is in: the revision the pointer
    // is on, else the newest revision (the tip), else the mail's own language.
    const backendAnswerLanguage =
        viewingRevision?.languageCode ??
        sortedRevisions[0]?.languageCode ??
        detectedLanguage ??
        null;

    const translate = useCallback(
        async (targetLanguageCode: string): Promise<string | null> => {
            if (!messageId) return null;
            const result = await translateMutation({
                variables: { messageId, targetLanguageCode },
            });
            return result.data?.messageTranslateProposedAnswer ?? null;
        },
        [messageId, translateMutation],
    );

    const {
        selectedLanguage,
        languageOptions,
        selectLanguage,
        isTranslating,
        display: languageDisplay,
        adoptText,
        answerReplaced,
    } = useAnswerLanguageSelection({
        messageId,
        answerText: proposedAnswer ?? null,
        answerLanguage: backendAnswerLanguage,
        detectedLanguage,
        streamText,
        synthesisActive,
        translate,
        onTranslated: onChanged,
        onError: onTranslationError,
    });

    // Rule 4.8: selecting a version whose text is not already on the client keeps
    // a loading indication until it arrives. In the normal case the text ships in
    // `revisions`, so the swap is immediate and no loading state appears at all.
    const [pendingRevisionId, setPendingRevisionId] = useState<string | null>(null);
    const isSelecting = pendingRevisionId !== null;

    const selectRevision = (revisionId: string) => {
        const rev = sortedRevisions.find((r) => r.id === revisionId);
        if (rev) {
            // Show it at once (rule 1.5), and move the selected language to that
            // version's language (rule 2.4) -- both are this operator action.
            adoptText(rev.proposedAnswer, rev.languageCode);
            setPendingRevisionId(null);
        } else {
            setPendingRevisionId(revisionId);
        }
        void selectRevisionMutation(revisionId).then(() => setPendingRevisionId(null));
    };

    const display: AnswerLanguageDisplay = isSelecting
        ? { text: null, isLoading: true }
        : languageDisplay;

    return {
        sortedRevisions,
        currentRevisionId,
        viewingRevision,
        isViewingOlderVersion,
        isSelecting,
        display,
        displayAnswer: display.text,
        selectRevision,
        getSourceLabel,
        adoptText,
        selectedLanguage,
        languageOptions,
        selectLanguage,
        isTranslating,
        // Rule 6.3: never offer a translation for an answer that does not exist.
        languageControlVisible: !!proposedAnswer || !!streamText || synthesisActive,
        answerReplaced,
    };
}
